import React, { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { DocumentUpload, Trash } from "iconsax-reactjs";
import { X } from "lucide-react";
import { Button } from "../../../../shared/ui";

export interface RecordTransferManualModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { proofFile: File | null; note: string }) => void;
  isSubmitting?: boolean;
}

export const RecordTransferManualModal: React.FC<
  RecordTransferManualModalProps
> = ({ open, onClose, onConfirm, isSubmitting }) => {
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProofFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({ proofFile, note });
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl focus:outline-hidden">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-[18px] font-semibold text-[#101828]">
                Record transfer manually
              </Dialog.Title>
              <Dialog.Description className="text-[13px] text-[#667085]">
                Upload proof of payment and update the transaction status.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-[#98A2B3] transition-colors hover:bg-[#F2F4F7] hover:text-[#344054]"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
            {/* Upload Box */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />

            {!proofFile ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#D0D5DD] bg-[#F8F9FC] p-6 text-center transition-colors hover:border-[#6F00FF] hover:bg-[#F9F5FF]"
              >
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#6F00FF] shadow-xs">
                  <DocumentUpload size={20} variant="Bold" color="#6F00FF" />
                </div>
                <span className="text-[13px] font-medium text-[#101828]">
                  Click to upload proof of payment
                </span>
                <span className="mt-0.5 text-[11px] text-[#667085]">
                  PDF, PNG, JPG (max 10MB)
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border border-[#EAECF0] bg-white p-3.5 shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F9F5FF] text-[#6F00FF]">
                    <DocumentUpload size={18} variant="Bold" color="#6F00FF" />
                  </div>
                  <div className="flex flex-col">
                    <span className="max-w-[220px] truncate text-[13px] font-medium text-[#101828]">
                      {proofFile.name}
                    </span>
                    <span className="text-[11px] text-[#667085]">
                      {(proofFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setProofFile(null)}
                  className="rounded p-1 text-[#98A2B3] hover:text-[#D92D20]"
                >
                  <Trash size={16} />
                </button>
              </div>
            )}

            {/* Note Input */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-[#344054]">
                Transfer note / reference
              </span>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter transaction bank reference or transfer remarks"
                className="w-full resize-none rounded-[8px] border border-transparent bg-[#F8F9FC] p-3 text-[13px] text-[#101828] placeholder-[#98A2B3] focus:border-[#6F00FF] focus:bg-white focus:outline-hidden"
              />
            </div>

            {/* Actions */}
            <div className="mt-2 flex items-center justify-end gap-3 border-t border-[#F2F4F7] pt-4">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-[#344054] transition-colors hover:text-[#101828]"
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-10 rounded-[8px] bg-[#020618] px-6 text-[14px] font-medium text-white shadow-xs transition-colors hover:bg-[#0f172a] disabled:bg-[#90A1B9] disabled:text-white disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Recording..." : "Record transfer"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
